import { Fragment, useState } from "react";
import { Transition } from "@headlessui/react";
import {
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ServerIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { classNames, formatDurationMs } from "@via/utils/helpers";
import type { McpToolCallInfo } from "common";

interface McpToolCallBubbleProps {
  mcpToolCall: McpToolCallInfo;
}

export const McpToolCallBubble = ({ mcpToolCall }: McpToolCallBubbleProps) => {
  const [showArgs, setShowArgs] = useState(false);

  const hasArgs =
    mcpToolCall.arguments && Object.keys(mcpToolCall.arguments).length > 0;
  const isAccepted = mcpToolCall.status === "accepted";

  return (
    <div
      className={classNames(
        "self-start max-w-[85%] rounded-lg border p-3 shadow-sm",
        isAccepted
          ? "border-green-200 bg-green-50/50"
          : "border-gray-200 bg-gray-50/50"
      )}>
      <div className="flex items-start gap-2.5">
        <div
          className={classNames(
            "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full",
            isAccepted ? "bg-green-100" : "bg-gray-100"
          )}>
          <WrenchScrewdriverIcon
            className={classNames(
              "h-3.5 w-3.5",
              isAccepted ? "text-green-600" : "text-gray-500"
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-gray-700">
              {mcpToolCall.toolName}
            </p>
            <span
              className={classNames(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
                isAccepted
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              )}>
              {isAccepted ? (
                <CheckCircleIcon className="h-3 w-3" />
              ) : (
                <XCircleIcon className="h-3 w-3" />
              )}
              {isAccepted ? "Accepted" : "Rejected"}
            </span>
          </div>

          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
            <ServerIcon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{mcpToolCall.serverName}</span>
          </div>

          {mcpToolCall.result && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px]">
              <span
                className={classNames(
                  "inline-flex items-center gap-1 font-medium",
                  mcpToolCall.result.success ? "text-green-600" : "text-red-600"
                )}>
                {mcpToolCall.result.success ? (
                  <CheckCircleIcon className="h-3 w-3" />
                ) : (
                  <XCircleIcon className="h-3 w-3" />
                )}
                {mcpToolCall.result.success ? "Success" : "Failed"}
              </span>
              <span className="inline-flex items-center gap-0.5 text-gray-400">
                <ClockIcon className="h-3 w-3" />
                {formatDurationMs(mcpToolCall.result.durationMs)}
              </span>
            </div>
          )}

          {hasArgs && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={() => setShowArgs(!showArgs)}
                aria-expanded={showArgs}
                className="flex items-center gap-1 text-[11px] text-gray-400 transition-colors duration-200 ease-in-out hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                {showArgs ? (
                  <ChevronUpIcon className="h-2.5 w-2.5" />
                ) : (
                  <ChevronDownIcon className="h-2.5 w-2.5" />
                )}
                {showArgs ? "Hide" : "Show"} arguments
              </button>
              <Transition
                as={Fragment}
                show={showArgs}
                enter="transition ease-out duration-150"
                enterFrom="transform opacity-0 -translate-y-1"
                enterTo="transform opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="transform opacity-100 translate-y-0"
                leaveTo="transform opacity-0 -translate-y-1">
                <pre className="mt-1 max-h-32 overflow-auto rounded-md bg-gray-800 p-2 text-[11px] text-gray-200 scrollbar-thin">
                  {JSON.stringify(mcpToolCall.arguments, null, 2)}
                </pre>
              </Transition>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
